import { Link } from "react-router-dom";
import { ArrowRight, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Reusable end-of-page / mid-page CTA banner.
 *
 * Variants:
 * - "navy" (default): solid navy background, secondary (yellow) button + ghost button
 * - "yellow": secondary background, primary button + outline
 * - "soft": muted background card, less aggressive (good for mid-section)
 */
export default function CtaBanner({
    eyebrow = "Próximo passo",
    title,
    description,
    primary = { to: "/contacto", label: "Peça o seu" },
    secondary = null,
    variant = "navy",
    className = "",
    testId = "cta-banner",
}) {
    const isNavy = variant === "navy";
    const isYellow = variant === "yellow";
    const isSoft = variant === "soft";

    const wrapper = cn(
        "relative overflow-hidden rounded-2xl",
        isNavy && "gradient-navy text-white shadow-xl",
        isYellow && "bg-secondary text-primary shadow-lg",
        isSoft && "bg-muted border border-border text-primary",
        className
    );

    const eyebrowCls = cn(
        "eyebrow",
        isNavy && "text-secondary",
        isYellow && "text-primary/70",
        isSoft && "text-muted-foreground"
    );

    const titleCls = cn(
        "mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.05]",
        isNavy && "text-white",
        (isYellow || isSoft) && "text-primary"
    );

    const descCls = cn(
        "mt-4 max-w-2xl text-base sm:text-lg leading-relaxed",
        isNavy && "text-white/80",
        isYellow && "text-primary/80",
        isSoft && "text-muted-foreground"
    );

    const primaryCls = cn(
        "inline-flex items-center justify-center gap-2 rounded-md px-7 py-3.5 text-sm font-bold shadow-sm transition hover:-translate-y-0.5",
        isNavy && "bg-secondary text-primary hover:bg-yellow-300",
        isYellow && "bg-primary text-primary-foreground hover:bg-primary/90",
        isSoft && "bg-primary text-primary-foreground hover:bg-primary/90"
    );

    const secondaryCls = cn(
        "inline-flex items-center justify-center gap-2 rounded-md px-7 py-3.5 text-sm font-bold transition hover:-translate-y-0.5 border",
        isNavy && "border-white/25 text-white hover:bg-white/10",
        isYellow && "border-primary/30 text-primary hover:bg-primary/10",
        isSoft && "border-primary/20 text-primary hover:bg-white"
    );

    return (
        <div className={wrapper} data-testid={testId}>
            {isNavy && <div className="absolute inset-0 grain-overlay opacity-40 pointer-events-none" />}
            {isNavy && (
                <div
                    aria-hidden
                    className="absolute -top-40 -right-40 h-[420px] w-[420px] rounded-full opacity-20 pointer-events-none"
                    style={{ background: "radial-gradient(circle, #FBBF24 0%, transparent 60%)" }}
                />
            )}
            <div className="relative px-6 py-8 sm:px-10 sm:py-10 lg:px-14 lg:py-12">
                {eyebrow && <span className={eyebrowCls}>{eyebrow}</span>}
                <h3 className={titleCls}>{title}</h3>
                {description && <p className={descCls}>{description}</p>}
                <div className="mt-8 flex flex-wrap items-center gap-3">
                    {primary && (
                        primary.external ? (
                            <a
                                href={primary.to}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={primaryCls}
                                data-testid={`${testId}-primary`}
                            >
                                {primary.icon ? <primary.icon className="h-4 w-4" /> : null}
                                {primary.label} <ArrowRight className="h-4 w-4" />
                            </a>
                        ) : (
                            <Link
                                to={primary.to}
                                className={primaryCls}
                                data-testid={`${testId}-primary`}
                            >
                                {primary.icon ? <primary.icon className="h-4 w-4" /> : null}
                                {primary.label} <ArrowRight className="h-4 w-4" />
                            </Link>
                        )
                    )}
                    {secondary && (
                        secondary.external ? (
                            <a
                                href={secondary.to}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={secondaryCls}
                                data-testid={`${testId}-secondary`}
                            >
                                {secondary.icon ? <secondary.icon className="h-4 w-4" /> : <MessageCircle className="h-4 w-4" />}
                                {secondary.label}
                            </a>
                        ) : (
                            <Link
                                to={secondary.to}
                                className={secondaryCls}
                                data-testid={`${testId}-secondary`}
                            >
                                {secondary.icon ? <secondary.icon className="h-4 w-4" /> : null}
                                {secondary.label}
                            </Link>
                        )
                    )}
                </div>
            </div>
        </div>
    );
}
